import React, { useState, useRef, useEffect } from 'react';
import {
  Play,
  Square,
  RotateCcw,
  Download,
  Upload,
  Copy,
  Settings,
  Maximize2,
  Minimize2,
  Terminal,
  FileCode,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  Bug,
  Plus
} from 'lucide-react';

interface CodeFile {
  id: string;
  name: string;
  language: string;
  content: string;
  isMain?: boolean;
}

interface TestCase {
  id: string;
  input: string;
  expectedOutput: string;
  description: string;
  isPublic: boolean;
}

interface ExecutionResult {
  output: string;
  error?: string;
  executionTime: number;
  memoryUsed: number;
  status: 'success' | 'error' | 'timeout';
  testResults?: {
    passed: number;
    total: number;
    details: Array<{
      testId: string;
      passed: boolean;
      actualOutput: string;
      expectedOutput: string;
      executionTime: number;
    }>;
  };
}

const CodeEditor: React.FC = () => {
  const [files, setFiles] = useState<CodeFile[]>([
    {
      id: 'main',
      name: 'ParkingLot.js',
      language: 'javascript',
      content: `// Parking Lot System Implementation
// Complete the following classes to implement a parking lot system

class Vehicle {
  constructor(licensePlate, type) {
    this.licensePlate = licensePlate;
    this.type = type; // 'motorcycle', 'car', 'truck'
    this.entryTime = new Date();
  }
  
  getSpotSize() {
    const sizes = {
      'motorcycle': 1,
      'car': 2,
      'truck': 4
    };
    return sizes[this.type] || 2;
  }
  
  getParkingFee(hours) {
    const rates = {
      'motorcycle': 5,
      'car': 10,
      'truck': 20
    };
    return (rates[this.type] || 10) * hours;
  }
}

class ParkingSpot {
  constructor(spotId, size, level) {
    this.spotId = spotId;
    this.size = size;
    this.level = level;
    this.isOccupied = false;
    this.parkedVehicle = null;
  }
  
  canFitVehicle(vehicle) {
    // TODO: Implement logic to check if vehicle can fit in this spot
    return false;
  }
  
  parkVehicle(vehicle) {
    // TODO: Implement logic to park vehicle in this spot
    return false;
  }
  
  removeVehicle() {
    // TODO: Implement logic to remove vehicle from spot
    return null;
  }
}

class Level {
  constructor(levelNumber) {
    this.levelNumber = levelNumber;
    this.spots = [];
    this.availableSpots = new Map();
  }
  
  addSpot(spot) {
    // TODO: Add spot to this level
  }
  
  findAvailableSpot(vehicle) {
    // TODO: Find available spot for vehicle
    return null;
  }
  
  parkVehicle(vehicle) {
    // TODO: Park vehicle in this level
    return null;
  }
}

class ParkingLot {
  constructor(name) {
    this.name = name;
    this.levels = [];
    this.tickets = new Map();
    this.ticketCounter = 1;
  }
  
  addLevel(level) {
    // TODO: Add level to parking lot
  }
  
  parkVehicle(vehicle) {
    // TODO: Park vehicle in parking lot
    return null;
  }
  
  removeVehicle(ticketId) {
    // TODO: Remove vehicle and calculate payment
    return null;
  }
}

// Test your implementation
function testParkingLot() {
  const parkingLot = new ParkingLot("Mall Parking");
  
  // Create a level with some spots
  const level1 = new Level(1);
  
  // Add spots of different sizes
  level1.addSpot(new ParkingSpot("L1-S1", 1, 1)); // Motorcycle
  level1.addSpot(new ParkingSpot("L1-S2", 2, 1)); // Car
  level1.addSpot(new ParkingSpot("L1-S3", 4, 1)); // Truck
  
  parkingLot.addLevel(level1);
  
  // Test vehicles
  const motorcycle = new Vehicle("M123", "motorcycle");
  const car = new Vehicle("C456", "car");
  const truck = new Vehicle("T789", "truck");
  
  // Try parking
  const ticket1 = parkingLot.parkVehicle(motorcycle);
  const ticket2 = parkingLot.parkVehicle(car);
  const ticket3 = parkingLot.parkVehicle(truck);
  
  console.log("Parking Results:");
  console.log("Motorcycle:", ticket1 ? "Parked" : "Failed");
  console.log("Car:", ticket2 ? "Parked" : "Failed");
  console.log("Truck:", ticket3 ? "Parked" : "Failed");
  
  return { parkingLot, tickets: [ticket1, ticket2, ticket3] };
}

// Run the test
const result = testParkingLot();`,
      isMain: true
    }
  ]);
  
  const [activeFileId, setActiveFileId] = useState('main');
  const [currentLanguage, setCurrentLanguage] = useState('javascript');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [theme, setTheme] = useState('vs-dark');
  const [showTestCases, setShowTestCases] = useState(false);
  
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  const testCases: TestCase[] = [
    {
      id: 'basic',
      input: '',
      expectedOutput: 'Motorcycle: Parked\nCar: Parked\nTruck: Parked',
      description: 'Basic parking functionality test',
      isPublic: true
    },
    {
      id: 'capacity',
      input: '',
      expectedOutput: 'Should handle parking lot capacity correctly',
      description: 'Test parking lot capacity limits',
      isPublic: true
    },
    {
      id: 'payment',
      input: '',
      expectedOutput: 'Should calculate correct parking fees',
      description: 'Test payment calculation',
      isPublic: false
    }
  ];

  const languages = [
    { id: 'javascript', name: 'JavaScript', extension: 'js', icon: '🟨' },
    { id: 'typescript', name: 'TypeScript', extension: 'ts', icon: '🔷' },
    { id: 'python', name: 'Python', extension: 'py', icon: '🐍' },
    { id: 'java', name: 'Java', extension: 'java', icon: '☕' },
    { id: 'cpp', name: 'C++', extension: 'cpp', icon: '⚡' }
  ];

  const templates = {
    javascript: `// JavaScript Implementation
class ParkingLot {
  constructor(name) {
    this.name = name;
    // TODO: Initialize your parking lot
  }
  
  // TODO: Implement your methods here
}

// Test your implementation
console.log("JavaScript Parking Lot System");`,
    
    typescript: `// TypeScript Implementation
interface Vehicle {
  licensePlate: string;
  type: 'motorcycle' | 'car' | 'truck';
  entryTime: Date;
}

class ParkingLot {
  private name: string;
  
  constructor(name: string) {
    this.name = name;
    // TODO: Initialize your parking lot
  }
  
  // TODO: Implement your methods here
}

// Test your implementation
console.log("TypeScript Parking Lot System");`,
    
    python: `# Python Implementation
from datetime import datetime
from typing import Optional, List, Dict
from enum import Enum

class VehicleType(Enum):
    MOTORCYCLE = "motorcycle"
    CAR = "car"
    TRUCK = "truck"

class Vehicle:
    def __init__(self, license_plate: str, vehicle_type: VehicleType):
        self.license_plate = license_plate
        self.vehicle_type = vehicle_type
        self.entry_time = datetime.now()
    
    def get_spot_size(self) -> int:
        sizes = {
            VehicleType.MOTORCYCLE: 1,
            VehicleType.CAR: 2,
            VehicleType.TRUCK: 4
        }
        return sizes.get(self.vehicle_type, 2)

class ParkingLot:
    def __init__(self, name: str):
        self.name = name
        # TODO: Initialize your parking lot
    
    # TODO: Implement your methods here

# Test your implementation
print("Python Parking Lot System")`,
    
    java: `// Java Implementation
import java.time.LocalDateTime;
import java.util.*;

enum VehicleType {
    MOTORCYCLE(1, 5),
    CAR(2, 10),
    TRUCK(4, 20);
    
    private final int size;
    private final int hourlyRate;
    
    VehicleType(int size, int hourlyRate) {
        this.size = size;
        this.hourlyRate = hourlyRate;
    }
    
    public int getSize() { return size; }
    public int getHourlyRate() { return hourlyRate; }
}

class Vehicle {
    private String licensePlate;
    private VehicleType type;
    private LocalDateTime entryTime;
    
    public Vehicle(String licensePlate, VehicleType type) {
        this.licensePlate = licensePlate;
        this.type = type;
        this.entryTime = LocalDateTime.now();
    }
    
    // Getters
    public String getLicensePlate() { return licensePlate; }
    public VehicleType getType() { return type; }
    public LocalDateTime getEntryTime() { return entryTime; }
}

public class ParkingLot {
    private String name;
    
    public ParkingLot(String name) {
        this.name = name;
        // TODO: Initialize your parking lot
    }
    
    // TODO: Implement your methods here
    
    public static void main(String[] args) {
        System.out.println("Java Parking Lot System");
        // Test your implementation
    }
}`,
    
    cpp: `// C++ Implementation
#include <iostream>
#include <string>
#include <vector>
#include <map>
#include <memory>
#include <chrono>

enum class VehicleType {
    MOTORCYCLE = 1,
    CAR = 2,
    TRUCK = 4
};

class Vehicle {
private:
    std::string licensePlate;
    VehicleType type;
    std::chrono::system_clock::time_point entryTime;

public:
    Vehicle(const std::string& licensePlate, VehicleType type) 
        : licensePlate(licensePlate), type(type), 
          entryTime(std::chrono::system_clock::now()) {}
    
    // Getters
    const std::string& getLicensePlate() const { return licensePlate; }
    VehicleType getType() const { return type; }
    auto getEntryTime() const { return entryTime; }
    
    int getSpotSize() const {
        return static_cast<int>(type);
    }
};

class ParkingLot {
private:
    std::string name;
    
public:
    ParkingLot(const std::string& name) : name(name) {
        // TODO: Initialize your parking lot
    }
    
    // TODO: Implement your methods here
};

int main() {
    std::cout << "C++ Parking Lot System" << std::endl;
    // Test your implementation
    return 0;
}`
  };

  const getCurrentFile = () => files.find(f => f.id === activeFileId) || files[0];

  const updateFileContent = (content: string) => {
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === activeFileId 
          ? { ...file, content }
          : file
      )
    );
  };

  const addNewFile = () => {
    const lang = languages.find(l => l.id === currentLanguage) || languages[0];
    const newFile: CodeFile = {
      id: `file-${Date.now()}`,
      name: `NewFile.${lang.extension}`,
      language: currentLanguage,
      content: templates[currentLanguage as keyof typeof templates] || '// New file'
    };
    
    setFiles(prev => [...prev, newFile]);
    setActiveFileId(newFile.id);
  };

  const deleteFile = (fileId: string) => {
    if (files.length <= 1) return;
    
    setFiles(prev => prev.filter(f => f.id !== fileId));
    if (activeFileId === fileId) {
      setActiveFileId(files[0].id);
    }
  };

  const executeCode = async () => {
    setIsExecuting(true);
    setExecutionResult(null);
    
    const currentFile = getCurrentFile();
    const startTime = Date.now();
    
    try {
      // Simulate code execution (in a real implementation, this would call a backend service)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (currentLanguage === 'javascript') {
        // For JavaScript, we can actually execute it
        const output: string[] = [];
        const customConsole = {
          log: (...args: any[]) => {
            output.push(args.map(arg => 
              typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
            ).join(' '));
          },
          error: (...args: any[]) => {
            output.push('ERROR: ' + args.map(arg => String(arg)).join(' '));
          }
        };

        try {
          // Create a function that runs the code with custom console
          // eslint-disable-next-line no-new-func
          const fn = new Function('console', currentFile.content);
          fn(customConsole);
          
          const executionTime = Date.now() - startTime;
          
          setExecutionResult({
            output: output.join('\n') || 'Code executed successfully (no output)',
            executionTime,
            memoryUsed: Math.floor(Math.random() * 1000) + 500, // Simulated
            status: 'success'
          });
        } catch (error) {
          setExecutionResult({
            output: '',
            error: error instanceof Error ? error.message : String(error),
            executionTime: Date.now() - startTime,
            memoryUsed: 0,
            status: 'error'
          });
        }
      } else {
        // For other languages, simulate execution
        const sampleOutputs = {
          python: 'Python Parking Lot System\nCode executed successfully!',
          java: 'Java Parking Lot System\nCompilation and execution completed.',
          cpp: 'C++ Parking Lot System\nCompiled and executed successfully.',
          typescript: 'TypeScript Parking Lot System\nCode transpiled and executed!'
        };
        
        setExecutionResult({
          output: sampleOutputs[currentLanguage as keyof typeof sampleOutputs] || 'Code executed successfully!',
          executionTime: Date.now() - startTime,
          memoryUsed: Math.floor(Math.random() * 1000) + 500,
          status: 'success'
        });
      }
    } catch (error) {
      setExecutionResult({
        output: '',
        error: 'Execution failed: ' + (error instanceof Error ? error.message : String(error)),
        executionTime: Date.now() - startTime,
        memoryUsed: 0,
        status: 'error'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const runTestCases = async () => {
    setIsExecuting(true);
    
    // Simulate running test cases
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const testResults = testCases.map(testCase => ({
      testId: testCase.id,
      passed: Math.random() > 0.3, // Random pass/fail for demo
      actualOutput: 'Actual test output...',
      expectedOutput: testCase.expectedOutput,
      executionTime: Math.floor(Math.random() * 100) + 50
    }));
    
    const passed = testResults.filter(r => r.passed).length;
    
    setExecutionResult({
      output: `Test Results: ${passed}/${testResults.length} passed`,
      executionTime: testResults.reduce((sum, r) => sum + r.executionTime, 0),
      memoryUsed: Math.floor(Math.random() * 1000) + 500,
      status: passed === testResults.length ? 'success' : 'error',
      testResults: {
        passed,
        total: testResults.length,
        details: testResults
      }
    });
    
    setIsExecuting(false);
  };

  const resetCode = () => {
    const template = templates[currentLanguage as keyof typeof templates];
    if (template) {
      updateFileContent(template);
    }
  };

  const downloadCode = () => {
    const currentFile = getCurrentFile();
    const blob = new Blob([currentFile.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentFile.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCode = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.js,.ts,.py,.java,.cpp,.txt';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          updateFileContent(content);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const copyCode = () => {
    const currentFile = getCurrentFile();
    navigator.clipboard.writeText(currentFile.content);
  };

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [executionResult]);

  return (
    <div className={`${isExpanded ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900' : ''} flex flex-col h-full`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <FileCode className="w-5 h-5 mr-2 text-blue-600" />
            Code Laboratory
          </h3>
          
          <div className="flex items-center space-x-2">
            {languages.map((lang) => (
              <button
                key={lang.id}
                onClick={() => setCurrentLanguage(lang.id)}
                className={`px-3 py-1 text-xs rounded-md border transition-all ${
                  currentLanguage === lang.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                }`}
              >
                <span className="mr-1">{lang.icon}</span>
                {lang.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowTestCases(!showTestCases)}
            className={`p-2 rounded-lg transition-colors ${
              showTestCases
                ? 'bg-green-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            title="Test Cases"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={isExpanded ? "Minimize" : "Expand"}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* File Tabs */}
      <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-1 flex-1 overflow-x-auto">
          {files.map((file) => (
            <div key={file.id} className="flex items-center">
              <button
                onClick={() => setActiveFileId(file.id)}
                className={`px-3 py-1 text-sm rounded-t-lg border-t border-l border-r transition-all ${
                  activeFileId === file.id
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {file.name}
                {file.isMain && <span className="ml-1 text-xs text-blue-600">*</span>}
              </button>
              {!file.isMain && (
                <button
                  onClick={() => deleteFile(file.id)}
                  className="ml-1 p-1 text-xs text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addNewFile}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Add File"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={uploadCode}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Upload"
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            onClick={downloadCode}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={copyCode}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Copy"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={resetCode}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-700">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Font Size</label>
              <select
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full p-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
              >
                <option value={12}>12px</option>
                <option value={14}>14px</option>
                <option value={16}>16px</option>
                <option value={18}>18px</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Theme</label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="w-full p-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
              >
                <option value="vs-dark">Dark</option>
                <option value="vs-light">Light</option>
                <option value="monokai">Monokai</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowSettings(false)}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Code Editor */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {getCurrentFile().name}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={executeCode}
                disabled={isExecuting}
                className="flex items-center space-x-2 px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
              >
                {isExecuting ? (
                  <>
                    <Square className="w-4 h-4" />
                    <span>Running...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    <span>Run</span>
                  </>
                )}
              </button>
              
              {showTestCases && (
                <button
                  onClick={runTestCases}
                  disabled={isExecuting}
                  className="flex items-center space-x-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Test</span>
                </button>
              )}
            </div>
          </div>
          
          <div className="flex-1 relative">
            <textarea
              ref={editorRef}
              value={getCurrentFile().content}
              onChange={(e) => updateFileContent(e.target.value)}
              className="w-full h-full p-4 font-mono text-sm bg-gray-900 text-gray-100 border-none outline-none resize-none"
              style={{ fontSize: `${fontSize}px` }}
              placeholder="Start coding your parking lot system..."
              spellCheck={false}
            />
          </div>
        </div>

        {/* Output Panel */}
        <div className="lg:w-1/3 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <Terminal className="w-4 h-4 mr-2" />
              Output
            </h4>
          </div>
          
          <div 
            ref={outputRef}
            className="flex-1 p-4 font-mono text-sm bg-gray-900 text-gray-100 overflow-y-auto"
          >
            {isExecuting ? (
              <div className="flex items-center space-x-2 text-yellow-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                <span>Executing code...</span>
              </div>
            ) : executionResult ? (
              <div className="space-y-4">
                {/* Execution Status */}
                <div className={`flex items-center space-x-2 ${
                  executionResult.status === 'success' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {executionResult.status === 'success' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span>
                    {executionResult.status === 'success' ? 'Execution Successful' : 'Execution Failed'}
                  </span>
                </div>

                {/* Execution Stats */}
                <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-3 h-3" />
                    <span>{executionResult.executionTime}ms</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Zap className="w-3 h-3" />
                    <span>{executionResult.memoryUsed}KB</span>
                  </div>
                </div>

                {/* Output */}
                {executionResult.output && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-400 mb-2">Output:</h5>
                    <pre className="text-gray-100 whitespace-pre-wrap bg-gray-800 p-3 rounded">
                      {executionResult.output}
                    </pre>
                  </div>
                )}

                {/* Error */}
                {executionResult.error && (
                  <div>
                    <h5 className="text-xs font-semibold text-red-400 mb-2 flex items-center">
                      <Bug className="w-3 h-3 mr-1" />
                      Error:
                    </h5>
                    <pre className="text-red-300 whitespace-pre-wrap bg-red-900/30 p-3 rounded border border-red-700">
                      {executionResult.error}
                    </pre>
                  </div>
                )}

                {/* Test Results */}
                {executionResult.testResults && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-400 mb-2">Test Results:</h5>
                    <div className="space-y-2">
                      {executionResult.testResults.details.map((test) => (
                        <div
                          key={test.testId}
                          className={`p-2 rounded border ${
                            test.passed 
                              ? 'bg-green-900/30 border-green-700 text-green-300'
                              : 'bg-red-900/30 border-red-700 text-red-300'
                          }`}
                        >
                          <div className="flex items-center justify-between text-xs">
                            <span>{test.testId}</span>
                            <span>{test.executionTime}ms</span>
                          </div>
                          {!test.passed && (
                            <div className="mt-1 text-xs">
                              <div>Expected: {test.expectedOutput}</div>
                              <div>Actual: {test.actualOutput}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8">
                <Terminal className="w-8 h-8 mx-auto mb-2" />
                <p>Click "Run" to execute your code</p>
              </div>
            )}
          </div>
        </div>

        {/* Test Cases Panel */}
        {showTestCases && (
          <div className="lg:w-1/4 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Test Cases</h4>
            </div>
            <div className="p-4 space-y-4">
              {testCases.filter(tc => tc.isPublic).map((testCase) => (
                <div key={testCase.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {testCase.description}
                  </h5>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <div className="mb-2">
                      <strong>Expected Output:</strong>
                      <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-x-auto">
                        {testCase.expectedOutput}
                      </pre>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="text-xs text-gray-600 dark:text-gray-400">
                + {testCases.filter(tc => !tc.isPublic).length} hidden test cases
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeEditor;
